// C.1 Этап 34 — реализация всех 23 v6 Действий Деуса (которые не были
// сделаны в старом коде: Взлом/Перегруппировка/Манипуляция/Выговор/Враждебное).
//
// Подход:
//   - Каждое Действие — отдельная функция resolveXxx(state, playerIdx, target, opts).
//   - Targeted-Действия объявлены через TARGETED_C1 и getTargetsC1.
//   - В deus/actions.js диспетчер: сначала пробуем C1 (по карте map),
//     если нет — fallback на старые (vzlom/peregruppirovka/manipulyatsiya/vygovor/vrazhdebnoe).
//
// Хелперы из engine/effects/helpers.js и engine/buffs.js используются по максимуму.
// Уникальная логика (велика жертва, захват рынка, захват контракта) — inline.

import { applyDamage } from '../../../combat.js';
import { applyHeroDamage } from '../../../hero-damage.js';
import { healUnit } from '../../livlife/heal.js';
import { applyPermanentBuff, applyTemporaryBuff } from '../../../buffs.js';
import { addPermanentKeyword } from '../../../keywords/permanent.js';
import { applyImmunity, applyImmuneFirstHit } from '../../../keywords/immune.js';
import { applySilenceWithTTL, applySilence } from '../../../keywords/silence.js';
import { summonUnit } from '../../global/summon.js';
import { destroyUnit } from '../../livlife/destroy.js';
import { mindControlUnit } from '../../../effects/mind-control.js';
import { dispatchUnitDied, registerOnEndOfTurnExtraHandler } from '../hooks.js';
import { addInfluence } from '../influence.js';

const STAZHER_ID    = 'deus_stazher';
const KORPOOHR_ID   = 'deus_korpoohranik';
const ZARAZHENY_ID  = 'deus_zarazheny_iskinom';

// ============================================================
// 1. Боевое снаряжение (epic, init=4): Герой +2/0/0 + pierce permanent.
// ============================================================
function resolveBoevoeSnaryazhenie(state, playerIdx) {
  const p = state.players[playerIdx];
  applyPermanentBuff(state, p.hero, { atk: 2, armor: 0, hp: 0 }, 'deus_boevoe_snaryazhenie', 'Боевое снаряжение');
  addPermanentKeyword(state, p.hero, 'pierce', 'deus_boevoe_snaryazhenie', 'Боевое снаряжение');
  state.log.add(`⚙ Боевое снаряжение [${p.name}]: Герой +2 атаки и Пробитие до конца матча`);
  return { ok: true };
}

// ============================================================
// 2. Боевой имплант (rare, init=3): Герой frenzy на 1 ход.
// ============================================================
function resolveBoevoyImplant(state, playerIdx) {
  const p = state.players[playerIdx];
  // 1 ход = до конца следующего хода (xlsx: until_end_of_next_turn). Используем
  // expiresAtTurn = currentTurn + 1, чтобы тик в Ф6 следующего хода снял.
  // Постоянного механизма «временный keyword» у нас нет — добавим permanent,
  // и зарегистрируем cleanup через EOT-handler. Чтобы не строить инфраструктуру
  // ради 3 карт, используем applyPermanentBuff с маркером src, а cleanup
  // через registerOnEndOfTurnExtraHandler с проверкой turn-стика.
  addPermanentKeyword(state, p.hero, 'frenzy', 'deus_boevoy_implant', 'Боевой имплант');
  scheduleHeroKeywordCleanup(state, playerIdx, 'frenzy', 'deus_boevoy_implant', state.currentTurn + 1);
  state.log.add(`⚙ Боевой имплант [${p.name}]: Герой получает Неистовство до конца следующего хода`);
  return { ok: true };
}

// ============================================================
// 3. Импланты «Хищник» (legendary, init=5): Герой +3/0/0 + pierce + frenzy permanent.
// ============================================================
function resolveImplantyKhishchnik(state, playerIdx) {
  const p = state.players[playerIdx];
  applyPermanentBuff(state, p.hero, { atk: 3, armor: 0, hp: 0 }, 'deus_implanty_khishchnik', 'Импланты «Хищник»');
  addPermanentKeyword(state, p.hero, 'pierce', 'deus_implanty_khishchnik', 'Импланты «Хищник»');
  addPermanentKeyword(state, p.hero, 'frenzy', 'deus_implanty_khishchnik', 'Импланты «Хищник»');
  state.log.add(`⚙ Импланты «Хищник» [${p.name}]: Герой +3 атаки, Пробитие, Неистовство до конца матча`);
  return { ok: true };
}

// ============================================================
// 4. Корп-сделка (rare, init=3): +3 Влияния + призыв Стажёра.
// ============================================================
function resolveKorpSdelka(state, playerIdx) {
  const p = state.players[playerIdx];
  addInfluence(state, playerIdx, 3, 'Корп-сделка');
  summonFirstFreeSlot(state, playerIdx, STAZHER_ID);
  state.log.add(`💼 Корп-сделка [${p.name}]: +3 Влияния, призыв Стажёра`);
  return { ok: true };
}

// ============================================================
// 5. Корп-рейд (rare, init=3): 4 урона вражескому юниту (не Герою).
// ============================================================
function resolveKorpreid(state, playerIdx, target) {
  if (!target || target.type !== 'unit') return { ok: false, reason: 'no_target' };
  const p = state.players[playerIdx];
  const opponent = state.players[1 - playerIdx];
  const before = { hp: target.hp, armor: target.armor };
  applyDamage(target, 4, { state, pierce: false, source: { id: 'deus_korpreid' } });
  state.log.add(`💥 Корп-рейд [${p.name}] → ${target.name_ru}: 4 урона (HP ${before.hp}→${target.hp}, броня ${before.armor}→${target.armor})`);
  if (target.hp <= 0) {
    opponent.removeFromField(target);
    state.log.add(`✗ ${target.name_ru} [${opponent.name}] погибает`);
    dispatchUnitDied(state, { ownerIdx: opponent.id, card: target, killer: null, killerIdx: playerIdx, killerWasCounter: false });
  }
  return { ok: true };
}

// ============================================================
// 6. Личное участие (legendary, init=5): урон = hero.atk цели (юнит или Герой), pierce, ignore avangard.
// ============================================================
function resolveLichnoeUchastie(state, playerIdx, target) {
  if (!target) return { ok: false, reason: 'no_target' };
  const p = state.players[playerIdx];
  const dmg = p.hero?.current?.atk ?? p.hero?.atk ?? 0;
  if (dmg <= 0) return { ok: false, reason: 'hero_atk_zero' };
  const opponent = state.players[1 - playerIdx];
  if (target === opponent.hero) {
    applyHeroDamage(state, opponent.id, dmg, { pierce: true });
    state.log.add(`💢 Личное участие [${p.name}] → Герой [${opponent.name}]: ${dmg} урона (Пробитие)`);
  } else {
    const before = { hp: target.hp, armor: target.armor };
    applyDamage(target, dmg, { state, pierce: true, source: { id: 'deus_lichnoe_uchastie' } });
    state.log.add(`💢 Личное участие [${p.name}] → ${target.name_ru}: ${dmg} урона (Пробитие, HP ${before.hp}→${target.hp})`);
    if (target.hp <= 0) {
      opponent.removeFromField(target);
      state.log.add(`✗ ${target.name_ru} [${opponent.name}] погибает`);
      dispatchUnitDied(state, { ownerIdx: opponent.id, card: target, killer: null, killerIdx: playerIdx, killerWasCounter: false });
    }
  }
  return { ok: true };
}

// ============================================================
// 7. Ликвидация контракта (rare, init=3): уничтожить своего atk≤2 + призыв Заражённого+Стажёра.
// ============================================================
function resolveLikvidatsiyaKontrakta(state, playerIdx, target) {
  const p = state.players[playerIdx];
  if (!target || target.ownerId !== playerIdx) return { ok: false, reason: 'no_target' };
  const atk = target.current?.atk ?? target.atk ?? 0;
  if (atk > 2) return { ok: false, reason: 'target_atk_too_high' };
  state.log.add(`📋 Ликвидация контракта [${p.name}]: уничтожение ${target.name_ru} (atk=${atk})`);
  destroyUnit(state, target, playerIdx);
  summonFirstFreeSlot(state, playerIdx, ZARAZHENY_ID);
  summonFirstFreeSlot(state, playerIdx, STAZHER_ID);
  return { ok: true };
}

// ============================================================
// 8. Медстраховка (rare, init=3): heal Герою 4.
// ============================================================
function resolveMedstrahovka(state, playerIdx) {
  const p = state.players[playerIdx];
  healUnit(state, p.hero, 4, { id: 'deus_medstrahovka' });
  state.log.add(`💊 Медстраховка [${p.name}]: Герой +4 ХП`);
  return { ok: true };
}

// ============================================================
// 9. Мега-сделка (epic, init=4): призыв Корпохранника + флаг 50% скидки на 3 призыва из стенда.
// ============================================================
function resolveMegaSdelka(state, playerIdx) {
  const p = state.players[playerIdx];
  summonFirstFreeSlot(state, playerIdx, KORPOOHR_ID);
  // Флаг скидки 50% на следующие 3 розыгрыша из стенда. Учитывается в playInfluenceCard.
  p.influenceDiscountRemaining = (p.influenceDiscountRemaining || 0) + 3;
  p.influenceDiscountPct = 50;
  state.log.add(`💼 Мега-сделка [${p.name}]: призыв Корпохранника + следующие 3 розыгрыша из стенда дешевле на 50%`);
  return { ok: true };
}

// ============================================================
// 10. Оперативный приказ (rare, init=3): Герой pierce 1 ход.
// ============================================================
function resolveOperativnyPrikaz(state, playerIdx) {
  const p = state.players[playerIdx];
  addPermanentKeyword(state, p.hero, 'pierce', 'deus_operativny_prikaz', 'Оперативный приказ');
  scheduleHeroKeywordCleanup(state, playerIdx, 'pierce', 'deus_operativny_prikaz', state.currentTurn + 1);
  state.log.add(`⚙ Оперативный приказ [${p.name}]: Герой получает Пробитие до конца следующего хода`);
  return { ok: true };
}

// ============================================================
// 11. Передовой щит (rare, init=3): Герой immune 1 ход (firstHit-вариант).
// ============================================================
function resolvePeredovoyShchit(state, playerIdx) {
  const p = state.players[playerIdx];
  applyImmuneFirstHit(state, p.hero, 'deus_peredovoy_shchit');
  state.log.add(`🛡 Передовой щит [${p.name}]: Герой получает Иммунитет до конца следующей атаки`);
  return { ok: true };
}

// ============================================================
// 12. Перевербовка (epic, init=4): уничтожить своего + mind_control врага atk≤5.
//      opts.allyTargetInstanceId — кого жертвуем (своего).
//      target — какого врага угоняем.
// ============================================================
function resolvePereverbovka(state, playerIdx, target, opts = {}) {
  const p = state.players[playerIdx];
  const allies = p.unitsOnField().filter(u => u.type === 'unit');
  if (allies.length === 0) return { ok: false, reason: 'no_ally' };
  const allyId = opts.allyTargetInstanceId;
  const ally = allyId ? allies.find(u => u.instanceId === allyId) : allies[0];
  if (!ally) return { ok: false, reason: 'ally_not_found' };
  if (!target || target.ownerId === playerIdx) return { ok: false, reason: 'no_enemy_target' };
  const enemyAtk = target.current?.atk ?? target.atk ?? 0;
  if (enemyAtk > 5) return { ok: false, reason: 'enemy_atk_too_high' };
  state.log.add(`📋 Перевербовка [${p.name}]: жертва ${ally.name_ru}, угон ${target.name_ru}`);
  destroyUnit(state, ally, playerIdx);
  if (!p.hasFreeSlot()) return { ok: false, reason: 'no_free_slot_after_sacrifice' };
  mindControlUnit(state, playerIdx, target);
  return { ok: true };
}

// ============================================================
// 13. Подпись смертника (rare, init=3): уничтожить своего + добрать 2 карты.
//      target — свой юнит (для жертвы).
// ============================================================
function resolvePodpisSmertnika(state, playerIdx, target) {
  const p = state.players[playerIdx];
  if (!target || target.ownerId !== playerIdx) return { ok: false, reason: 'no_ally_target' };
  destroyUnit(state, target, playerIdx);
  drawN(state, playerIdx, 2);
  state.log.add(`📋 Подпись смертника [${p.name}]: жертва ${target.name_ru}, +2 карты`);
  return { ok: true };
}

// ============================================================
// 14. Расстрельная команда (epic, init=4): 6 урона цели (юнит или Герой), respect Авангард.
// ============================================================
function resolveRasstrelnayaKomanda(state, playerIdx, target) {
  if (!target) return { ok: false, reason: 'no_target' };
  const p = state.players[playerIdx];
  const opponent = state.players[1 - playerIdx];
  // Respect Авангард: если цель — Герой, но у врага есть видимые юниты — нельзя.
  if (target === opponent.hero) {
    const hasUnits = opponent.unitsOnField().some(u =>
      u.id !== 'deus_inf_spyashy_agent' && !(u.keywords || []).includes('stealth')
    );
    if (hasUnits) return { ok: false, reason: 'avangard_protects' };
    applyHeroDamage(state, opponent.id, 6, { pierce: false });
    state.log.add(`💥 Расстрельная команда [${p.name}] → Герой [${opponent.name}]: 6 урона`);
  } else {
    const before = { hp: target.hp, armor: target.armor };
    applyDamage(target, 6, { state, pierce: false, source: { id: 'deus_rasstrelnaya_komanda' } });
    state.log.add(`💥 Расстрельная команда [${p.name}] → ${target.name_ru}: 6 урона (HP ${before.hp}→${target.hp}, броня ${before.armor}→${target.armor})`);
    if (target.hp <= 0) {
      opponent.removeFromField(target);
      state.log.add(`✗ ${target.name_ru} [${opponent.name}] погибает`);
      dispatchUnitDied(state, { ownerIdx: opponent.id, card: target, killer: null, killerIdx: playerIdx, killerWasCounter: false });
    }
  }
  return { ok: true };
}

// ============================================================
// 15. Реабилитация (legendary, init=5): heal Героя to full.
// ============================================================
function resolveReabilitatsiya(state, playerIdx) {
  const p = state.players[playerIdx];
  const max = p.hero.base?.hp ?? p.hero.hp;
  const need = Math.max(0, max - (p.hero.current?.hp ?? p.hero.hp));
  if (need > 0) healUnit(state, p.hero, need, { id: 'deus_reabilitatsiya' });
  state.log.add(`💊 Реабилитация [${p.name}]: Герой восстановлен полностью`);
  return { ok: true };
}

// ============================================================
// 16. Реструктуризация (common, init=1): союзнику +0/1/1 permanent.
// ============================================================
function resolveRestrukturizatsiya(state, playerIdx, target) {
  if (!target || target.ownerId !== playerIdx) return { ok: false, reason: 'no_ally_target' };
  const p = state.players[playerIdx];
  applyPermanentBuff(state, target, { atk: 0, armor: 1, hp: 1 }, 'deus_restrukturizatsiya', 'Реструктуризация');
  state.log.add(`⚙ Реструктуризация [${p.name}] → ${target.name_ru}: +0/1/1`);
  return { ok: true };
}

// ============================================================
// 17. Слияние (common, init=1): добрать 2 + 1 урона своему Герою.
// ============================================================
function resolveSliyanie(state, playerIdx) {
  const p = state.players[playerIdx];
  drawN(state, playerIdx, 2);
  applyHeroDamage(state, playerIdx, 1, { pierce: false });
  state.log.add(`📋 Слияние [${p.name}]: +2 карты, -1 ХП своему Герою`);
  return { ok: true };
}

// ============================================================
// 18. Тактический штурм (rare, init=3): Герой +2/0/0 на 1 ход.
// ============================================================
function resolveTakticheskiyShturm(state, playerIdx) {
  const p = state.players[playerIdx];
  // «до конца следующего хода»: applyTemporaryBuff снимается в НАЧАЛЕ хода
  // expiresAtTurn (expireTemporaryBuffs в Ф1). Чтобы бафф жил текущий ход T И
  // следующий T+1, expiresAtTurn = T+2 (currentTurn+2) — как документировано в
  // inektsiya_adrenalina.js. Раньше было +1 → бафф снимался в начале T+1 и
  // фактически жил только текущий ход (QA-фикс).
  const expiresAtTurn = (state.currentTurn ?? 1) + 2;
  applyTemporaryBuff(state, p.hero, { atk: 2, armor: 0, hp: 0 }, expiresAtTurn, 'deus_takticheskiy_shturm', 'Тактический штурм');
  state.log.add(`⚙ Тактический штурм [${p.name}]: Герой +2 атаки до конца следующего хода`);
  return { ok: true };
}

// ============================================================
// 19. Тайный совет (rare, init=3): максимум Влияния +2 permanent (10→12).
// ============================================================
function resolveTaynySovet(state, playerIdx) {
  const p = state.players[playerIdx];
  p.influenceMaxBonus = (p.influenceMaxBonus || 0) + 2;
  state.log.add(`💼 Тайный совет [${p.name}]: максимум Влияния увеличен на +2 (до ${10 + p.influenceMaxBonus})`);
  return { ok: true };
}

// ============================================================
// 20. Указ Совета (rare, init=3): Молчание вражескому юниту permanent.
// ============================================================
function resolveUkazSoveta(state, playerIdx, target) {
  if (!target || target.ownerId === playerIdx) return { ok: false, reason: 'no_enemy_target' };
  const p = state.players[playerIdx];
  applySilence(state, target, 'deus_ukaz_soveta');
  state.log.add(`📋 Указ Совета [${p.name}] → ${target.name_ru}: Молчание`);
  return { ok: true };
}

// ============================================================
// 21. Великая жертва (legendary, init=5): до 3 своих atk≥2 → +3 Влияния и 3 урона Герою врага за каждого.
//      Минимальная реализация: автоматически берём до 3 случайных подходящих своих юнитов.
//      Multi-target picker для UI — отдельная задача.
// ============================================================
function resolveVelikayaZhertva(state, playerIdx, opts = {}) {
  const p = state.players[playerIdx];
  const opponent = state.players[1 - playerIdx];
  const eligible = p.unitsOnField().filter(u =>
    u.type === 'unit' && (u.current?.atk ?? u.atk ?? 0) >= 2
  );
  if (eligible.length === 0) return { ok: false, reason: 'no_eligible_allies' };
  let victims;
  const sel = opts?.selectedInstanceIds;
  if (Array.isArray(sel) && sel.length > 0) {
    // 2d: выбор игрока — только валидные (atk≥2, свои, живые) из выбранных, максимум 3.
    const eligIds = new Set(eligible.map(u => u.instanceId));
    victims = sel
      .filter(id => eligIds.has(id))
      .map(id => eligible.find(u => u.instanceId === id))
      .filter(Boolean)
      .slice(0, 3);
    if (victims.length === 0) return { ok: false, reason: 'no_valid_selection' };
  } else {
    // Бот/без выбора: случайно до 3.
    const pool = [...eligible];
    for (let i = pool.length - 1; i > 0; i--) {
      const j = state.rng?.int ? state.rng.int(i + 1) : Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    victims = pool.slice(0, 3);
  }
  let bonusInf = 0;
  for (const v of victims) {
    destroyUnit(state, v, playerIdx);
    bonusInf += 3;
    applyHeroDamage(state, opponent.id, 3, { pierce: true });
  }
  if (bonusInf > 0) addInfluence(state, playerIdx, bonusInf, 'Великая жертва');
  state.log.add(`💢 Великая жертва [${p.name}]: ${victims.length} юнит(ов), +${bonusInf} Влияния, ${victims.length * 3} урона Герою врага`);
  return { ok: true, sacrificed: victims.length };
}

// ============================================================
// 22. Захват контракта (epic, init=4): mind_control врага на 3 хода.
//      По истечении — возвращается врагу с текущими статами.
// ============================================================
function resolveZahvatKontrakta(state, playerIdx, target) {
  if (!target || target.ownerId === playerIdx) return { ok: false, reason: 'no_enemy_target' };
  const p = state.players[playerIdx];
  const originalOwnerIdx = target.ownerId;
  const returnAtTurn = (state.currentTurn ?? 1) + 3;
  const r = mindControlUnit(state, playerIdx, target);
  if (!r?.ok) return { ok: false, reason: r?.reason || 'mc_failed' };
  // Помечаем для возврата.
  target.temporaryMindControlReturnAtTurn = returnAtTurn;
  target.temporaryMindControlOriginalOwnerIdx = originalOwnerIdx;
  state.log.add(`📋 Захват контракта [${p.name}] → ${target.name_ru}: угон на 3 хода (вернётся на ходу ${returnAtTurn})`);
  return { ok: true };
}

// ============================================================
// 23. Захват рынка (legendary, init=5): 2 случайные карты из стенда Влияния БЕСПЛАТНО.
// ============================================================
function resolveZahvatRynka(state, playerIdx) {
  const p = state.players[playerIdx];
  // Используем динамический импорт, чтобы избежать circular между actions-c1
  // и influence-cards.
  return import('../influence-cards.js').then(infm => {
    const offer = p.influenceOffer || [];
    if (offer.length === 0) {
      state.log.add(`📋 Захват рынка [${p.name}]: оффер пуст`);
      return { ok: true, free: 0 };
    }
    // Перемешать оффер.
    const pool = [...offer];
    for (let i = pool.length - 1; i > 0; i--) {
      const j = state.rng?.int ? state.rng.int(i + 1) : Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    let played = 0;
    for (const cardId of pool.slice(0, 2)) {
      // Временно даём 999 Влияния и обнуляем счётчик после.
      const before = p.influence;
      p.influence = 999;
      const r = infm.playInfluenceCard(state, playerIdx, cardId, null);
      // Восстанавливаем именно так: вернём prev баланс плюс компенсация любого «вытащенного» (через cost).
      // playInfluenceCard через chargeInfluence уменьшает p.influence. Так как мы передали 999,
      // итог = 999 - cost. Вернём исходный baseline.
      p.influence = before;
      if (r?.ok) played++;
    }
    state.log.add(`📋 Захват рынка [${p.name}]: разыграно бесплатно ${played} карт(ы) из стенда`);
    return { ok: true, free: played };
  }).catch(e => {
    console.warn('Захват рынка ошибка:', e);
    return { ok: false, reason: 'internal_error' };
  });
}

// ============================================================
// === Регистрация ===
// ============================================================
const RESOLVERS_C1 = {
  'deus_boevoe_snaryazhenie':      (s, i, t, o) => resolveBoevoeSnaryazhenie(s, i),
  'deus_boevoy_implant':           (s, i, t, o) => resolveBoevoyImplant(s, i),
  'deus_implanty_khishchnik':      (s, i, t, o) => resolveImplantyKhishchnik(s, i),
  'deus_korp_sdelka':              (s, i, t, o) => resolveKorpSdelka(s, i),
  'deus_korpreid':                 (s, i, t, o) => resolveKorpreid(s, i, t),
  'deus_lichnoe_uchastie':         (s, i, t, o) => resolveLichnoeUchastie(s, i, t),
  'deus_likvidatsiya_kontrakta':   (s, i, t, o) => resolveLikvidatsiyaKontrakta(s, i, t),
  'deus_medstrahovka':             (s, i, t, o) => resolveMedstrahovka(s, i),
  'deus_mega_sdelka':              (s, i, t, o) => resolveMegaSdelka(s, i),
  'deus_operativny_prikaz':        (s, i, t, o) => resolveOperativnyPrikaz(s, i),
  'deus_peredovoy_shchit':         (s, i, t, o) => resolvePeredovoyShchit(s, i),
  'deus_pereverbovka':             (s, i, t, o) => resolvePereverbovka(s, i, t, o),
  'deus_podpis_smertnika':         (s, i, t, o) => resolvePodpisSmertnika(s, i, t),
  'deus_rasstrelnaya_komanda':     (s, i, t, o) => resolveRasstrelnayaKomanda(s, i, t),
  'deus_reabilitatsiya':           (s, i, t, o) => resolveReabilitatsiya(s, i),
  'deus_restrukturizatsiya':       (s, i, t, o) => resolveRestrukturizatsiya(s, i, t),
  'deus_sliyanie':                 (s, i, t, o) => resolveSliyanie(s, i),
  'deus_takticheskiy_shturm':      (s, i, t, o) => resolveTakticheskiyShturm(s, i),
  'deus_tayny_sovet':              (s, i, t, o) => resolveTaynySovet(s, i),
  'deus_ukaz_soveta':              (s, i, t, o) => resolveUkazSoveta(s, i, t),
  'deus_velikaya_zhertva':         (s, i, t, o) => resolveVelikayaZhertva(s, i, o),
  'deus_zahvat_kontrakta':         (s, i, t, o) => resolveZahvatKontrakta(s, i, t),
  'deus_zahvat_rynka':             (s, i, t, o) => resolveZahvatRynka(s, i)
};

// Targeted: какие требуют выбор цели.
const TARGETED_C1 = new Set([
  'deus_korpreid',                      // вражеский юнит (не Герой)
  'deus_lichnoe_uchastie',              // юнит ИЛИ Герой врага
  'deus_likvidatsiya_kontrakta',        // свой юнит atk≤2
  'deus_pereverbovka',                  // вражеский юнит atk≤5 (опционально + allyTargetInstanceId)
  'deus_podpis_smertnika',              // свой юнит
  'deus_rasstrelnaya_komanda',          // юнит или Герой (respect Авангард)
  'deus_restrukturizatsiya',            // свой юнит
  'deus_ukaz_soveta',                   // вражеский юнит
  'deus_zahvat_kontrakta'               // вражеский юнит
]);

// Targets провайдеры.
function getTargetsC1(state, playerIdx, cardId) {
  const p = state.players[playerIdx];
  const opponent = state.players[1 - playerIdx];
  const collectAllyUnits = () => {
    const r = [];
    for (const row of p.field) for (const u of row || []) if (u && u.type === 'unit') r.push(u);
    return r;
  };
  const collectEnemyUnits = () => {
    const r = [];
    for (const row of opponent.field) for (const u of row || []) if (u && u.type === 'unit') r.push(u);
    return r;
  };
  const collectEnemyAll = () => {
    const r = collectEnemyUnits();
    if (opponent.hero?.hp > 0) r.push(opponent.hero);
    return r;
  };
  switch (cardId) {
    case 'deus_korpreid':                return collectEnemyUnits();
    case 'deus_lichnoe_uchastie':        return collectEnemyAll();
    case 'deus_likvidatsiya_kontrakta':  return collectAllyUnits().filter(u => (u.current?.atk ?? u.atk ?? 0) <= 2);
    case 'deus_pereverbovka':            return collectEnemyUnits().filter(u => (u.current?.atk ?? u.atk ?? 0) <= 5);
    case 'deus_podpis_smertnika':        return collectAllyUnits();
    case 'deus_rasstrelnaya_komanda':    return collectEnemyAll();
    case 'deus_restrukturizatsiya':      return collectAllyUnits();
    case 'deus_ukaz_soveta':             return collectEnemyUnits();
    case 'deus_zahvat_kontrakta':        return collectEnemyUnits();
    default:                              return [];
  }
}

// === Утилиты ===
function summonFirstFreeSlot(state, playerIdx, cardId) {
  const p = state.players[playerIdx];
  for (let line = 0; line < 2; line++) {
    const row = p.field?.[line] || [];
    for (let s = 0; s < row.length; s++) {
      if (row[s] === null) {
        summonUnit(state, playerIdx, cardId, line, s);
        return true;
      }
    }
  }
  return false;
}

function drawN(state, playerIdx, n) {
  const p = state.players[playerIdx];
  for (let i = 0; i < n; i++) {
    if (typeof p.drawOne === 'function') {
      // БАГ-фикс (Этап 56.53): p.drawOne() ВОЗВРАЩАЕТ карту, но НЕ кладёт её в
      // руку (это делает вызывающий — см. phaseDraw). Раньше результат
      // игнорировался → карта уходила из колоды, но в руку не попадала
      // (Корп.слияние / Подпись смертника «добери 2» не работали).
      const c = p.drawOne();
      if (c) {
        if ((p.hand?.length ?? 0) < 10) p.hand.push(c);
        else p.discard?.push(c); // рука полна — карта сгорает в сброс
      }
    } else if (p.deck?.length > 0 && (p.hand?.length ?? 0) < 10) {
      p.hand.push(p.deck.shift());
    }
  }
}

// Регистр временных keyword'ов Героя — снимется на указанном ходу в EOT.
// Использует общий EOT-handler.
const _pendingHeroKwCleanups = [];
function scheduleHeroKeywordCleanup(state, playerIdx, keyword, sourceId, expiresAtTurn) {
  _pendingHeroKwCleanups.push({ playerIdx, keyword, sourceId, expiresAtTurn });
}
registerOnEndOfTurnExtraHandler((state) => {
  if (_pendingHeroKwCleanups.length === 0) return;
  const currentTurn = state.currentTurn ?? 0;
  for (let i = _pendingHeroKwCleanups.length - 1; i >= 0; i--) {
    const entry = _pendingHeroKwCleanups[i];
    if (currentTurn >= entry.expiresAtTurn) {
      const p = state.players?.[entry.playerIdx];
      if (p?.hero?.permanentKeywordSources) {
        // Снять keyword от конкретного source. Через прямую работу с источниками.
        const srcs = p.hero.permanentKeywordSources[entry.keyword];
        if (srcs) {
          srcs.delete(entry.sourceId);
          if (srcs.size === 0) {
            delete p.hero.permanentKeywordSources[entry.keyword];
            // Если в card.keywords всё ещё есть — снять.
            const idx = (p.hero.keywords || []).indexOf(entry.keyword);
            if (idx >= 0) p.hero.keywords.splice(idx, 1);
          }
        }
      }
      _pendingHeroKwCleanups.splice(i, 1);
    }
  }
});

// Tick возврата временно угнанных юнитов (Захват контракта).
registerOnEndOfTurnExtraHandler((state) => {
  const currentTurn = state.currentTurn ?? 0;
  for (const p of state.players) {
    for (const row of p.field) {
      for (const u of row || []) {
        if (!u) continue;
        if (u.temporaryMindControlReturnAtTurn == null) continue;
        if (currentTurn < u.temporaryMindControlReturnAtTurn) continue;
        const original = u.temporaryMindControlOriginalOwnerIdx;
        if (original == null || original === u.ownerId) {
          // Что-то не так — стираем флаги.
          u.temporaryMindControlReturnAtTurn = null;
          u.temporaryMindControlOriginalOwnerIdx = null;
          continue;
        }
        const targetPlayer = state.players[original];
        if (!targetPlayer.hasFreeSlot()) {
          state.log?.add(`📋 Захват контракта: у ${targetPlayer.name} нет места — ${u.name_ru} остаётся под угоном`);
          continue;
        }
        // Возвращаем mindControl'ом на оригинального владельца.
        mindControlUnit(state, original, u);
        state.log?.add(`📋 Захват контракта: ${u.name_ru} возвращён к ${targetPlayer.name}`);
        u.temporaryMindControlReturnAtTurn = null;
        u.temporaryMindControlOriginalOwnerIdx = null;
      }
    }
  }
});

// === Экспортируемое API ===
export function needsTargetC1(cardId) {
  return TARGETED_C1.has(cardId);
}
export function getActionTargetsC1(state, playerIdx, cardId) {
  return getTargetsC1(state, playerIdx, cardId);
}
export function resolveActionC1(state, playerIdx, cardId, target, opts = {}) {
  const fn = RESOLVERS_C1[cardId];
  if (!fn) return null; // не наш — пусть диспетчер пробует старые.
  return fn(state, playerIdx, target, opts);
}
export function isC1Action(cardId) {
  return cardId in RESOLVERS_C1;
}
